import React, { useEffect, useRef, useState } from 'react';
import { ClubPanel, PanelHead, PillButton, SegmentedPills, Switch, TagPill, INK, MUTED, SUBTLE, BODY, CHIP, INSET, CYAN, RULE, inputStyle, textareaStyle } from '../club/clubUi';
import { EMBED_DEFAULTS, SCRIPT_URL, CSS_HOOKS, CSS_EXAMPLE, embedSnippet, embedRenderOpts, isHex } from './embedModel';
import { API_BASE } from '../../utils/apiBase';

/**
 * EmbedPanel — EMBED in the Pro dashboard's SETTINGS: put the page's events on the owner's own
 * website. Two modes on a ring tab, SIMPLE (theme · layout · how many) and ADVANCED (every knob
 * plus a custom CSS box); both show a LIVE PREVIEW drawn by the real script
 * (`/embed/portal-events.js`, loaded from this origin and told which API to call), THE CODE to
 * copy, and HOW TO INSTALL in three steps. Tickets stay on Portal — the button opens the event here.
 */
const PRESET_LIMITS = [[3, '3'], [6, '6'], [12, '12'], [24, '24']];
const FIELD = { fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: SUBTLE };

function Field({ label, hint, children, testId }) {
  return (
    <div className="flex flex-col" style={{ gap: 8 }} data-testid={testId}>
      <span className="flex items-baseline justify-between" style={{ gap: 10 }}>
        <span style={FIELD}>{label}</span>
        {hint && <span style={{ fontSize: 10, color: SUBTLE }}>{hint}</span>}
      </span>
      {children}
    </div>
  );
}

/** Loads the embed script once from this origin; resolves with window.PortalEmbed. */
function loadEmbedScript() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.PortalEmbed?.render) return Promise.resolve(window.PortalEmbed);
  if (!window.__portalEmbedLoading) {
    window.__portalEmbedLoading = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = `${window.location.origin}/embed/portal-events.js`;
      s.async = true;
      s.onload = () => resolve(window.PortalEmbed || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return window.__portalEmbedLoading;
}

export default function EmbedPanel({ slug, pageName, phone = false, say }) {
  const [mode, setMode] = useState('simple');
  const [opts, setOpts] = useState(EMBED_DEFAULTS);
  const [accentText, setAccentText] = useState(EMBED_DEFAULTS.accent);
  const [previewState, setPreviewState] = useState('loading'); // loading | ready | failed
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);
  const sdkRef = useRef(null);
  const timer = useRef(null);
  const set = (patch) => setOpts((o) => ({ ...o, ...patch }));

  const snippet = embedSnippet(slug, opts);

  // The preview is the real thing: the script draws into the box exactly as it will on their site.
  useEffect(() => {
    let alive = true;
    loadEmbedScript().then((sdk) => {
      if (!alive) return;
      sdkRef.current = sdk;
      setPreviewState(sdk ? 'ready' : 'failed');
    });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (previewState !== 'ready' || !previewRef.current || !sdkRef.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // A re-render only re-fetches when the data options change; the script dedupes by sequence anyway.
      sdkRef.current.render(previewRef.current, embedRenderOpts(slug, opts, API_BASE));
    }, 180);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewState, slug, opts.theme, opts.layout, opts.limit, opts.accent, opts.font, opts.radius, opts.button, opts.poster, opts.brand, opts.past, opts.css]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      say?.('CODE COPIED');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      say?.('SELECT THE CODE AND COPY IT');
    }
  };

  const onAccent = (v) => { setAccentText(v); if (isHex(v)) set({ accent: v.toUpperCase() }); };

  const styleControls = (
    <>
      <div className="grid" style={{ gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 14 }}>
        <Field label="THEME" testId="embed-theme">
          <SegmentedPills options={[['dark', 'DARK'], ['light', 'LIGHT']]} value={opts.theme} onChange={(v) => set({ theme: v })} height={36} ring />
        </Field>
        <Field label="LAYOUT" testId="embed-layout">
          <SegmentedPills options={[['list', 'LIST'], ['grid', 'GRID']]} value={opts.layout} onChange={(v) => set({ layout: v })} height={36} ring />
        </Field>
        <Field label="HOW MANY" hint="upcoming, soonest first" testId="embed-limit">
          <SegmentedPills options={PRESET_LIMITS} value={Number(opts.limit)} onChange={(v) => set({ limit: v })} height={36} ring />
        </Field>
        {mode === 'advanced' && (
          <Field label="FONT" testId="embed-font">
            <SegmentedPills options={[['portal', 'PORTAL MONO'], ['inherit', 'YOUR SITE’S']]} value={opts.font} onChange={(v) => set({ font: v })} height={36} ring />
          </Field>
        )}
      </div>
      {mode === 'advanced' && (
        <>
          <div className="grid" style={{ gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 14 }}>
            <Field label="ACCENT" hint="date line · button on hover" testId="embed-accent">
              <span className="flex items-center" style={{ gap: 10 }}>
                <input type="color" aria-label="Accent colour" value={isHex(opts.accent) ? opts.accent : '#FF00AA'} onChange={(e) => onAccent(e.target.value)}
                  style={{ width: 48, height: 48, padding: 0, border: 'none', borderRadius: '50%', background: 'none', cursor: 'pointer' }} />
                <input aria-label="Accent hex" value={accentText} onChange={(e) => onAccent(e.target.value)} maxLength={7} spellCheck={false}
                  style={{ ...inputStyle, flex: 1, fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase' }} data-testid="embed-accent-hex" />
              </span>
            </Field>
            <Field label="CORNERS" hint={`${opts.radius}px`} testId="embed-radius">
              <input type="range" min={0} max={32} step={2} value={Number(opts.radius)} onChange={(e) => set({ radius: Number(e.target.value) })} aria-label="Corner roundness"
                style={{ width: '100%', height: 48, accentColor: INK }} />
            </Field>
            <Field label="BUTTON SAYS" testId="embed-button">
              <input value={opts.button} onChange={(e) => set({ button: e.target.value.slice(0, 24) })} placeholder={EMBED_DEFAULTS.button} aria-label="Button text" maxLength={24}
                style={{ ...inputStyle, fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase' }} data-testid="embed-button-text" />
            </Field>
            <div className="flex flex-col" style={{ gap: 10 }}>
              {[
                ['poster', 'SHOW POSTERS', opts.poster !== 'hide', (on) => set({ poster: on ? 'show' : 'hide' })],
                ['brand', 'SHOW “TICKETS BY PORTAL”', opts.brand !== 'hide', (on) => set({ brand: on ? 'show' : 'hide' })],
                ['past', 'INCLUDE PAST EVENTS', !!opts.past, (on) => set({ past: on })],
              ].map(([k, l, on, onChange]) => (
                <span key={k} className="flex items-center justify-between" style={{ gap: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em' }}>{l}</span>
                  <Switch on={on} onChange={onChange} label={l} testId={`embed-switch-${k}`} />
                </span>
              ))}
            </div>
          </div>
          <Field label="CUSTOM CSS" hint="optional — your rules win over ours" testId="embed-css">
            <textarea value={opts.css} onChange={(e) => set({ css: e.target.value })} placeholder={CSS_EXAMPLE} spellCheck={false} rows={7} aria-label="Custom CSS"
              style={{ ...textareaStyle, fontSize: 12, lineHeight: 1.6, minHeight: 150 }} data-testid="embed-css-box" />
            <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
              <PillButton variant="tertiary" size={32} fontSize={9} onClick={() => set({ css: CSS_EXAMPLE })} data-testid="embed-css-example">START FROM AN EXAMPLE</PillButton>
              {opts.css && <PillButton variant="ghost" size={32} fontSize={9} onClick={() => set({ css: '' })}>CLEAR</PillButton>}
            </div>
            <div style={{ padding: '12px 16px', borderRadius: 18, background: INSET }}>
              <span style={{ ...FIELD, display: 'block', paddingBottom: 8 }}>WHAT YOU CAN STYLE</span>
              {CSS_HOOKS.map(([k, d]) => (
                <span key={k} className="flex flex-wrap" style={{ gap: 8, padding: '4px 0', fontSize: 11, lineHeight: 1.5 }}>
                  <code style={{ fontWeight: 800, color: INK }}>{k}</code>
                  <span style={{ color: MUTED }}>{d}</span>
                </span>
              ))}
            </div>
          </Field>
          <PillButton variant="ghost" size={32} fontSize={9} onClick={() => { setOpts(EMBED_DEFAULTS); setAccentText(EMBED_DEFAULTS.accent); }} style={{ alignSelf: 'flex-start' }} data-testid="embed-reset">
            BACK TO PORTAL’S LOOK
          </PillButton>
        </>
      )}
    </>
  );

  return (
    <ClubPanel phone={phone} style={{ gap: 18 }} data-testid="embed-panel">
      <div className="flex flex-col" style={{ gap: 6 }}>
        <PanelHead title="EMBED" right="YOUR EVENTS ON YOUR OWN SITE" />
        <span style={{ fontSize: 12, lineHeight: 1.65, color: BODY }}>
          Show {pageName ? <b>{pageName}</b> : 'your page'}’s events on your own website. It keeps itself up to date, and every
          <b> {opts.button || 'BUY TICKETS'}</b> opens the event on Portal — tickets, money and the guest list stay here.
        </span>
      </div>

      <div className="flex" style={{ gap: 6 }} role="tablist" aria-label="Embed builder">
        {[['simple', 'SIMPLE'], ['advanced', 'ADVANCED']].map(([k, l]) => (
          <PillButton key={k} role="tab" aria-selected={mode === k} variant={mode === k ? 'primary' : 'ghost'} size={36} fontSize={10} onClick={() => setMode(k)}
            style={mode === k ? { background: 'linear-gradient(#000,#000) padding-box, conic-gradient(from 0deg, #FF00AA, #FFD400, #00FFFF, #7A00FF, #FF00AA) border-box', border: '2px solid transparent' } : { boxShadow: `inset 0 0 0 2px ${CHIP}` }}
            data-testid={`embed-mode-${k}`}>
            {l}
          </PillButton>
        ))}
      </div>

      {styleControls}

      <div className="flex flex-col" style={{ gap: 8 }}>
        <span className="flex items-center justify-between" style={{ gap: 10 }}>
          <span style={FIELD}>LIVE PREVIEW</span>
          <span style={{ fontSize: 10, color: SUBTLE }}>exactly what your visitors get</span>
        </span>
        <div style={{ padding: phone ? 10 : 16, borderRadius: 20, background: opts.theme === 'light' ? '#E5E7EB' : '#1F1F1F', overflow: 'hidden' }} data-testid="embed-preview-frame">
          <div ref={previewRef} data-testid="embed-preview">
            {previewState !== 'ready' && (
              <span style={{ display: 'block', padding: 28, textAlign: 'center', fontSize: 9, fontWeight: 800, letterSpacing: '.16em', color: SUBTLE }}>
                {previewState === 'failed' ? 'PREVIEW COULD NOT LOAD — THE CODE BELOW STILL WORKS' : 'LOADING PREVIEW…'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col" style={{ gap: 8 }}>
        <span className="flex items-center justify-between" style={{ gap: 10 }}>
          <span style={FIELD}>THE CODE</span>
          <PillButton size={32} fontSize={9} variant={copied ? 'cyan' : 'primary'} onClick={copy} data-testid="embed-copy">{copied ? 'COPIED' : 'COPY CODE'}</PillButton>
        </span>
        <textarea readOnly value={snippet} rows={snippet.split('\n').length + 1} onFocus={(e) => e.target.select()} aria-label="Embed code" spellCheck={false}
          style={{ ...textareaStyle, background: '#000', color: '#fff', fontSize: 11, lineHeight: 1.6, resize: 'none', whiteSpace: 'pre', overflowX: 'auto' }} data-testid="embed-snippet" />
      </div>

      <div className="flex flex-col" style={{ gap: 10, paddingTop: 14, borderTop: RULE }} data-testid="embed-install">
        <span style={FIELD}>HOW TO INSTALL — ABOUT A MINUTE</span>
        {[
          ['COPY THE CODE', 'Tap COPY CODE above. That is the whole thing — no account keys, no plugins.'],
          ['PASTE IT INTO YOUR SITE', 'Open the page where you want your events and add a code / HTML block, then paste. Squarespace: a Code block. Wix: Embed → Embed HTML. WordPress: a Custom HTML block. Shopify: a Custom Liquid section. Webflow: an Embed element. Any plain HTML page: anywhere inside <body>.'],
          ['PUBLISH', 'Save and publish the page. Your events appear in place and update on their own; new events show up without touching the code again.'],
        ].map(([t, d], i) => (
          <span key={t} className="flex" style={{ gap: 12 }}>
            <span className="flex flex-shrink-0 items-center justify-center rounded-full" style={{ width: 28, height: 28, background: i === 2 ? CYAN : INK, color: i === 2 ? INK : '#fff', fontSize: 11, fontWeight: 800 }}>{i + 1}</span>
            <span className="flex min-w-0 flex-col" style={{ gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em' }}>{t}</span>
              <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY }}>{d}</span>
            </span>
          </span>
        ))}
        <span className="flex flex-wrap items-center" style={{ gap: 10, paddingTop: 6 }}>
          <TagPill bg={CHIP} size={8}>GOOD TO KNOW</TagPill>
          <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>
            Only public, live events on this page are shown. Restyle any time — change the options here and paste the new code, or add your own CSS; the script lives at <code style={{ color: INK }}>{SCRIPT_URL}</code>.
          </span>
        </span>
      </div>
    </ClubPanel>
  );
}
